/**
 * Presentation of Money as text.
 *
 * The ONLY place `Intl` is permitted inside `core/`. Nothing here computes
 * anything — a formatter that rounds is a second calculation engine, which
 * is precisely the defect this architecture exists to prevent.
 */
import type { Money, Quantity } from './types';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `12345678` → `"₹12,34,567.78"` (Indian grouping: lakh, crore). */
export function formatMoney(value: Money, withSymbol = true): string {
  const text = inr.format(value / 100);
  return withSymbol ? text : text.replace('₹', '');
}

/** Amount without the symbol, for table columns headed "Amount". */
export function formatAmount(value: Money): string {
  return inr.format(value / 100).replace('₹', '').trim();
}

/** Rate column, typically 2 dp: `103664407` → `"10,36,644.07"`. */
export function formatRate(value: Money): string {
  return inr.format(value / 100).replace('₹', '').trim();
}

/** Quantity at the unit's precision: `750` at 2 dp → `"0.75"`. */
export function formatQuantity(value: Quantity, precision = 3): string {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  }).format(value / 1000);
}

/** Basis points as a percentage: `1800` → `"18"`, `1250` → `"12.5"`. */
export function formatRateBp(bp: number): string {
  const text = String(bp / 100);
  return text.replace(/\.0+$/, '');
}

/**
 * Plain rupees, ungrouped, for an EDITABLE field: `35000` → `"350.00"`.
 *
 * Deliberately not `formatAmount`, which produces Indian-grouped text
 * ("12,34,567.78"). That is right for a table cell and wrong for the value of
 * an `<input>`: the user would have to delete the separators before typing, and
 * a stray comma in a rate is a `RangeError` waiting to happen. This is the
 * inverse of `parseMoney`, and the two are the only sanctioned path between a
 * rate and a text box.
 *
 * No `toFixed` here either — core formats money, features do not.
 */
export function rupeesText(value: Money): string {
  const negative = value < 0;
  const abs = Math.abs(value);
  const whole = Math.trunc(abs / 100);
  const paise = abs % 100;
  return `${negative ? '-' : ''}${whole}.${String(paise).padStart(2, '0')}`;
}
